import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { tx, txr } from "../lib/tx";

type Stage = "hotspot" | "browser" | "download" | "install" | "capture";

const CAPTURES = [
  { device: "Sofia's phone", service: "mail.google.com", protocol: "HTTPS", result: "Encrypted — password not visible", safe: true },
  { device: "Mateo's laptop", service: "legacy-photo-share.test", protocol: "FTP", result: "mateo.guest / poolphotos26", safe: false },
  { device: "Guest tablet", service: "intranet-demo.test", protocol: "HTTP", result: "visitor17 / resort-demo", safe: false },
  { device: "Elena's iPad", service: "mail.google.com", protocol: "HTTPS", result: "Certificate rejected — no credentials sent", safe: true },
] as const;

export function HolidayRogueLab({
  onDone,
  onStageChange,
}: {
  onDone: () => void;
  onStageChange?: (stage: string) => void;
}) {
  const [stage, setStage] = useState<Stage>("hotspot");
  const [ssid, setSsid] = useState("");
  const [installed, setInstalled] = useState(false);
  const [captures, setCaptures] = useState(0);

  useEffect(() => onStageChange?.(`rogue_lab_${stage}`), [onStageChange, stage]);

  useEffect(() => {
    if (stage !== "download") return;
    const timer = window.setTimeout(() => setStage("install"), 1800);
    return () => window.clearTimeout(timer);
  }, [stage]);

  useEffect(() => {
    if (stage !== "capture") return;
    setCaptures(0);
    const timers = CAPTURES.map((_, index) =>
      window.setTimeout(() => setCaptures(index + 1), 900 + index * 1150),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [stage]);

  const hotspotReady = ssid.trim() === "GrandHotel_WiFi";

  return createPortal(
    <section className={`holiday-rogue-lab hrl-stage-${stage}`}>
      <header className="hrl-top">
        <div><span>{tx("ATTACKER LAB")}</span><strong>{tx("How a rogue access point is prepared")}</strong></div>
        <small>{tx("Controlled simulation only")}</small>
      </header>

      {stage === "hotspot" && (
        <div className="hrl-window hrl-settings">
          <aside><b>{tx("Windows Settings")}</b><span>{tx("Network & internet")}</span><span className="on">{tx("Mobile hotspot")}</span><span>{tx("Wi-Fi")}</span><span>{tx("VPN")}</span></aside>
          <main>
            <small>{tx("NETWORK & INTERNET")}</small>
            <h2>{tx("Mobile hotspot")}</h2>
            <p>{tx("Share this laptop's internet connection with nearby devices.")}</p>
            <div className="hrl-toggle-row"><div><b>{tx("Mobile hotspot")}</b><span>{tx("Off")}</span></div><i /></div>
            <label>
              {tx("Network name")}
              <input
                autoFocus
                value={ssid}
                onChange={(event) => setSsid(event.target.value)}
                placeholder={tx("Type the hotel network name")}
              />
            </label>
            <div className="hrl-copy-name">
              <span>{tx("Target name seen nearby")}</span>
              <code>{tx("GrandHotel_WiFi")}</code>
              <button type="button" onClick={() => setSsid("GrandHotel_WiFi")}>{tx("Use this name")}</button>
            </div>
            <button
              type="button"
              className={`hrl-next${hotspotReady ? " cf-pulse-target" : ""}`}
              disabled={!hotspotReady}
              onClick={() => setStage("browser")}
            >
              {tx("Turn on copied hotspot")}
              {hotspotReady && <em>{tx("Click here")}</em>}
            </button>
          </main>
        </div>
      )}

      {stage === "browser" && (
        <div className="hrl-window hrl-browser">
          <div className="hrl-tabs"><b>{tx("NetworkMiner — NETRESEC")}</b><i>＋</i></div>
          <div className="hrl-address"><span>←　↻</span><b>https://www.netresec.com/?page=NetworkMiner</b></div>
          <main>
            <span className="hrl-pickaxe">{tx("NM")}</span>
            <div>
              <small>{tx("OFFICIAL NETRESEC WEBSITE")}</small>
              <h2>{tx("NetworkMiner 3.1")}</h2>
              <p>{tx("Network forensics software that can inventory hosts and extract artifacts from captured network traffic.")}</p>
              <div className="hrl-fact">{txr("<0>Important:</0> NetworkMiner does not decrypt ordinary HTTPS traffic. It can expose credentials only when a protocol sends them without encryption, or after a separate trusted interception proxy has decrypted the traffic.", {}, [<b />])}</div>
              <button type="button" className="hrl-next cf-pulse-target" onClick={() => setStage("download")}>
                {txr("Download from official website <0>Click here</0>", {}, [<em />])}
              </button>
            </div>
          </main>
        </div>
      )}

      {stage === "download" && (
        <div className="hrl-window hrl-download">
          <span className="hrl-file">{tx("ZIP")}</span>
          <h2>{tx("Downloading NetworkMiner 3.1")}</h2>
          <p>{tx("Source: www.netresec.com")}</p>
          <div><i /></div>
          <small>{tx("Verifying the publisher and archive…")}</small>
        </div>
      )}

      {stage === "install" && (
        <div className="hrl-window hrl-installer">
          <aside><span>{tx("NM")}</span><b>{tx("NetworkMiner")}</b><small>{tx("Network Forensics Analysis Tool")}</small></aside>
          <main>
            <small>{tx("SETUP")}</small>
            <h2>{installed ? tx("Installation complete") : tx("Ready to install")}</h2>
            <p>{installed ? tx("NetworkMiner is ready to inspect traffic from the copied hotspot.") : tx("Install the downloaded application in this controlled training lab.")}</p>
            <dl>
              <div><dt>{tx("Publisher")}</dt><dd>{tx("NETRESEC AB")}</dd></div>
              <div><dt>{tx("Version")}</dt><dd>3.1</dd></div>
              <div><dt>{tx("Mode")}</dt><dd>{tx("Live capture")}</dd></div>
            </dl>
            {!installed ? (
              <button
                type="button"
                className="hrl-next cf-pulse-target"
                onClick={() => {
                  setInstalled(true);
                }}
              >
                {txr("Install <0>Click here</0>", {}, [<em />])}
              </button>
            ) : (
              <button type="button" className="hrl-next cf-pulse-target" onClick={() => setStage("capture")}>
                {txr("Launch NetworkMiner <0>Click here</0>", {}, [<em />])}
              </button>
            )}
          </main>
        </div>
      )}

      {stage === "capture" && (
        <div className="hrl-window hrl-miner">
          <header><span>{tx("NetworkMiner 3.1")}</span><b>{tx("Live capture: GrandHotel_WiFi")}</b><i>{tx("● CAPTURING")}</i></header>
          <nav>{txr("Hosts　Files　Images　Messages　　<0>Credentials</0>　　Sessions　DNS", {}, [<b />])}</nav>
          <div className="hrl-miner-body">
            <aside>
              <small>{tx("DEVICES JOINING")}</small>
              {CAPTURES.slice(0, captures).map((row, index) => (
                <div key={row.device}><span>{index % 2 ? "▰" : "▯"}</span><b>{row.device}</b><small>{row.protocol}</small></div>
              ))}
            </aside>
            <main>
              <div className="hrl-lab-banner"><b>{tx("SIMULATED LAB TRAFFIC")}</b><span>{tx("No real guest data is being captured.")}</span></div>
              <div className="hrl-capture-head"><span>{tx("Device")}</span><span>{tx("Service")}</span><span>{tx("Protocol")}</span><span>{tx("What the attacker sees")}</span></div>
              {CAPTURES.slice(0, captures).map((row) => (
                <div className={`hrl-capture-row${row.safe ? " is-encrypted" : " is-exposed"}`} key={`${row.device}-${row.service}`}>
                  <span>{row.device}</span><span>{row.service}</span><b>{row.protocol}</b><code>{row.result}</code>
                </div>
              ))}
              {captures < CAPTURES.length ? (
                <div className="hrl-listening"><i /> {tx("Waiting for connected devices and traffic…")}</div>
              ) : (
                <div className="hrl-capture-lesson">
                  <h3>{tx("A copied Wi-Fi name creates the opportunity. Encryption still matters.")}</h3>
                  <p>{tx("Plaintext HTTP, FTP and other legacy protocols can expose data directly. Normal HTTPS passwords stay encrypted unless the victim also trusts an attacker's certificate or bypasses a certificate warning—exactly why Elena's Google warning mattered.")}</p>
                  <button type="button" className="hrl-next cf-pulse-target" onClick={onDone}>{txr("Return to Elena's story <0>Click here</0>", {}, [<em />])}</button>
                </div>
              )}
            </main>
          </div>
        </div>
      )}
    </section>,
    document.body,
  );
}
